import { createContext, useContext } from "react";
import type { RefObject } from "react";

import { makeControlContext } from "@frontend/contexts";

import type { FieldProps } from "./Field.types";

export type FieldContextValue = Pick<
  FieldProps,
  "orientation" | "size" | "labelAlign" | "labelBlockAlign" | "errorDisplay"
> & {
  grouped: boolean;
};

export function makeFieldContext({
  orientation = "vertical",
  size,
  labelAlign = "left",
  labelBlockAlign = "center",
  errorDisplay = "block",
  grouped = false,
}: Partial<FieldContextValue>): Required<FieldContextValue> {
  return {
    orientation,
    ...makeControlContext({ size }),
    labelAlign,
    labelBlockAlign,
    errorDisplay,
    grouped,
  };
}

export const FieldContext = createContext<Required<FieldContextValue>>(
  makeFieldContext({}),
);

export function useFieldContext() {
  return useContext(FieldContext);
}

/**
 * Set by a Field showing its error in a tooltip: where to anchor the bubble,
 * and whether the field is being hovered or holds the focus.
 */
export type FieldErrorTooltipValue = {
  anchor: RefObject<HTMLDivElement | null>;
  active: boolean;
};

export const FieldErrorTooltipContext =
  createContext<FieldErrorTooltipValue | null>(null);

export function useFieldErrorTooltip(): FieldErrorTooltipValue | null {
  return useContext(FieldErrorTooltipContext);
}

const CONTROL_SELECTOR =
  ':scope > *:not([data-slot="field-description"]):not([data-slot="field-error"])';

/**
 * The widget a Field wraps, whatever it is: the bubble points at the control
 * rather than at the whole field, label included.
 */
export function fieldControl(root: HTMLElement | null): Element | null {
  const content = root?.querySelector('[data-slot="field-content"]');

  return content?.querySelector(CONTROL_SELECTOR) ?? root;
}
